/**
 * Lightweight reactive i18n. Strings are keyed by their English text, so wrapping
 * a label in `t('Speed gauge')` returns the Turkish when the language is 'tr' and
 * the original English otherwise — no separate key catalogue to maintain. Reading
 * `i18n.lang` (a $state) inside `t()` during render makes every `{t(...)}` update
 * the moment the language toggles.
 *
 * Turkish follows language-fidelity rules: native phrasing, no Tarzanca/plaza dili.
 */

export type Lang = 'en' | 'tr';

const TR: Record<string, string> = {
  // Airspace data freshness
  'Data age unknown': 'Veri yaşı bilinmiyor',
  Data: 'Veri',
  'just now': 'az önce',
  'h ago': 'saat önce',
  'd ago': 'gün önce',
  'Temporary restrictions are not shown: the data is too old to be relied on. Check İHATTYS.':
    'Geçici kısıtlamalar gösterilmiyor: veri güvenilmeyecek kadar eski. İHATTYS üzerinden kontrol edin.',
  // Map controls. MapLibre injects these itself and ships only English; they are also the controls'
  // accessible names, so leaving them was a screen reader reading English inside a Turkish page.
  'Zoom in': 'Yakınlaştır',
  'Zoom out': 'Uzaklaştır',
  'Reset bearing to north': 'Döndürmek için sürükleyin, kuzeye dönmek için tıklayın',
  'Find my location': 'Konumumu bul',
  'Location not available': 'Konum alınamıyor',
  'Toggle attribution': 'Kaynak bilgisini aç/kapat',
  'Close popup': 'Balonu kapat',
  // Three causes, three answers: one message would send someone who revoked permission outdoors to
  // look for sky.
  'Location permission is denied. Allow it for this site in your browser settings, then try again.':
    'Konum izni reddedilmiş. Tarayıcı ayarlarından bu siteye izin verip yeniden deneyin.',
  'Locating timed out. Try again — outdoors it is usually faster.':
    'Konum bulma zaman aşımına uğradı. Yeniden deneyin — açık alanda genelde daha hızlıdır.',
  'Your location could not be determined. GPS may be off, or there may be no signal indoors.':
    'Konumunuz belirlenemedi. GPS kapalı olabilir ya da kapalı alanda sinyal alınamıyor olabilir.',
  // Header / app chrome
  'Drone Player': 'Drone Oynatıcı',
  Metric: 'Metrik',
  Imperial: 'İmperyal',
  Language: 'Dil',
  Units: 'Birimler',
  // Timecode alignment (settings + per-clip)
  'Video timecode alignment': 'Video zaman kodu hizalama',
  'Added videos sync by their timecode against the flight — by default the recording START. Files whose name starts with one of these prefixes are treated as the recording END instead (drone footage and screen recordings finalise the file when recording stops). Override any clip on the timeline.':
    'Eklenen videolar, uçuşa göre zaman koduyla eşitlenir — öntanımlı olarak kaydın BAŞLANGICINA göre. Adı bu öneklerden biriyle başlayan dosyalar, kaydın SONU olarak ele alınır (drone görüntüleri ve ekran kayıtları, kayıt durunca sonlanır). Zaman çizelgesinde her klip için değiştirilebilir.',
  'None — every video aligns by its start.': 'Yok — her video başlangıcına göre hizalanır.',
  'add a prefix, e.g. DJI_': 'önek ekle, örn. DJI_',
  Timecode: 'Zaman kodu',
  'Timecode anchor': 'Zaman kodu dayanağı',
  Start: 'Başlangıç',
  End: 'Son',
  'creation_time marks the recording start (camera/phone)': 'creation_time kaydın başlangıcını gösterir (kamera/telefon)',
  'creation_time marks the recording end (drone/screen recording)': 'creation_time kaydın sonunu gösterir (drone/ekran kaydı)',
  'align manually': 'elle hizala',
  Exported: 'Dışa aktarıldı',
  'Export produced no output — try a different resolution or a shorter range.':
    'Dışa aktarma çıktı üretmedi — farklı bir çözünürlük ya da daha kısa bir aralık deneyin.',
  'Can’t play this video': 'Bu video oynatılamıyor',
  'The browser can’t decode it — often HEVC/H.265 (common on phones and some drones). Try an H.264 export of the clip.':
    'Tarayıcı çözemiyor — genelde HEVC/H.265 (telefonlarda ve bazı dronelarda yaygın). Klibin H.264 dışa aktarımını deneyin.',
  'Can’t decode this clip (maybe HEVC)': 'Bu klip çözülemiyor (belki HEVC)',
  'Graded hero video — the timeline anchor': 'Derecelenmiş ana video — zaman çizelgesi dayanağı',
  'graded hero (timeline anchor)': 'derecelenmiş ana video (zaman çizelgesi dayanağı)',
  'Remove the main video': 'Ana videoyu kaldır',
  'Add lane': 'Şerit ekle',
  'New lane': 'Yeni şerit',
  'Add an empty lane — or drag a clip here to give it its own lane':
    'Boş bir şerit ekle — ya da bir klibi buraya sürükleyip kendi şeridine taşı',
  'This video has no audio track': 'Bu videoda ses yok',
  'Mute this video’s audio': 'Bu videonun sesini kapat',
  'Unmute this video’s audio': 'Bu videonun sesini aç',
  'Mute/unmute audio': 'Sesi kapat/aç',
  'Undo timeline edit': 'Zaman çizelgesi düzenlemesini geri al',
  'Timeline & video gestures': 'Zaman çizelgesi ve video hareketleri',
  'Drag flight → timeline': 'Uçuşu → zaman çizelgesine sürükle',
  'Add that video as a synced track': 'O videoyu eşitlenmiş şerit olarak ekler',
  'Drag clip → Flights panel': 'Klibi → Uçuşlar paneline sürükle',
  'Remove it from the timeline': 'Zaman çizelgesinden kaldırır',
  'Pinch / Ctrl-scroll': 'Kıstır / Ctrl-kaydır',
  'Zoom the timeline · double-click to fit': 'Zaman çizelgesini yakınlaştır · sığdırmak için çift tıkla',
  'Shift-drag a clip': 'Klibi Shift ile sürükle',
  'Move across lanes, keeping its alignment': 'Hizasını koruyarak şeritler arasında taşır',
  'Alt-drag a clip': 'Klibi Alt ile sürükle',
  'Disable snapping for pixel-exact placement': 'Piksel hassasiyetli yerleştirme için yapışmayı kapatır',
  'Scroll on the video': 'Videoda kaydır',
  'Zoom · drag to pan · double-click fullscreen': 'Yakınlaştır · sürükleyerek kaydır · çift tıkla tam ekran',
  'The video timecode doesn’t match this flight — placed at the start; align it by hand.':
    'Video zaman kodu bu uçuşla uyuşmuyor — başa yerleştirildi; elle hizalayın.',
  'Clear all flights': 'Tüm uçuşları temizle',
  'Refresh flights': 'Uçuşları yenile',
  'Refresh flights — scan open folders for new recordings': 'Uçuşları yenile — açık klasörleri yeni kayıtlar için tara',
  'Remove all flights from the list?': 'Tüm uçuşlar listeden kaldırılsın mı?',
  // --- Tooltips, chart labels & body text (Türkçe denetimi) ---
  Close: 'Kapat',
  'Drag to move': 'Taşımak için sürükle',
  'Heading distribution': 'Yön dağılımı',
  'Open this flight': 'Bu uçuşu aç',
  'RGB colour histogram': 'RGB renk histogramı',
  'Resize split': 'Bölmeyi yeniden boyutlandır',
  'Row stride': 'Satır aralığı',
  'Seek here': 'Buraya atla',
  Settings: 'Ayarlar',
  'Wind direction': 'Rüzgar yönü',
  min: 'min',
  max: 'maks',
  'Wind: need varied headings': 'Rüzgar: çeşitli yönlerde uçuş gerek',
  'Drop to place': 'Yerleştirmek için bırak',
  Colour: 'Renk',
  default: 'varsayılan',
  Size: 'Boyut',
  avg: 'ort',
  video: 'video',
  map: 'harita',
  'Indexing flights…': 'Uçuşlar indeksleniyor…',
  'No flights match': 'Eşleşen uçuş yok',
  // Media filters: type (video/photo), time of day (sun phases), weekday, month, numeric ranges
  'Media filters': 'Medya filtreleri',
  Videos: 'Videolar',
  Photos: 'Fotoğraflar',
  Photo: 'Fotoğraf',
  'My photos': 'Fotoğraflarım',
  'Videos only': 'Yalnızca videolar',
  'All media': 'Tüm medya',
  'All media table': 'Tüm medya tablosu',
  'All media table (t)': 'Tüm medya tablosu (t)',
  'Filter media…': 'Medyayı süz…',
  'Filter media': 'Medyayı süz',
  Capture: 'Çekim',
  'Nothing matches — widen a filter or reset.': 'Hiçbir şey eşleşmiyor — bir filtreyi genişletin ya da sıfırlayın.',
  'Nothing matches — check the media filters in the panel.': 'Eşleşen yok — paneldeki medya filtrelerini gözden geçirin.',
  'Time of day': 'Günün saati',
  'Day of week': 'Haftanın günü',
  Month: 'Ay',
  active: 'etkin',
  'Quick pick': 'Hızlı seçim',
  'Clock window': 'Saat aralığı',
  From: 'Başlangıç',
  To: 'Bitiş',
  minutes: 'dakika',
  'Golden hour': 'Altın saat',
  'Blue hour': 'Mavi saat',
  Daylight: 'Gündüz',
  Dark: 'Karanlık',
  Weekdays: 'Hafta içi',
  Weekend: 'Hafta sonu',
  Winter: 'Kış',
  Spring: 'İlkbahar',
  Summer: 'Yaz',
  Autumn: 'Sonbahar',
  Monday: 'Pazartesi',
  Tuesday: 'Salı',
  Wednesday: 'Çarşamba',
  Thursday: 'Perşembe',
  Friday: 'Cuma',
  Saturday: 'Cumartesi',
  Sunday: 'Pazar',
  January: 'Ocak',
  February: 'Şubat',
  March: 'Mart',
  April: 'Nisan',
  May: 'Mayıs',
  June: 'Haziran',
  July: 'Temmuz',
  August: 'Ağustos',
  September: 'Eylül',
  October: 'Ekim',
  November: 'Kasım',
  December: 'Aralık',
  // Sun phases — the names a photographer uses, not literal translations of the astronomy terms.
  Night: 'Gece',
  'Dawn (blue hour)': 'Şafak (mavi saat)',
  'Sunrise (golden hour)': 'Gün doğumu (altın saat)',
  Morning: 'Sabah',
  Midday: 'Öğle',
  Afternoon: 'İkindi',
  'Sunset (golden hour)': 'Gün batımı (altın saat)',
  'Dusk (blue hour)': 'Alacakaranlık (mavi saat)',
  'sun below −6°': 'güneş −6° altında',
  'rising, −6° … −4°': 'yükselirken, −6° … −4°',
  'rising, −4° … +6°': 'yükselirken, −4° … +6°',
  'rising, above +6°': 'yükselirken, +6° üstünde',
  'within 1 h of solar noon': 'güneş öğlesine 1 saat kala/sonra',
  'setting, above +6°': 'alçalırken, +6° üstünde',
  'setting, +6° … −4°': 'alçalırken, +6° … −4°',
  'setting, −4° … −6°': 'alçalırken, −4° … −6°',
  'Phases follow the sun’s real elevation at the takeoff point, so “sunset” is correct in every season.':
    'Evreler, kalkış noktasındaki güneşin gerçek yüksekliğine göre belirlenir; böylece “gün batımı” her mevsimde doğru olur.',
  'Runs past midnight — one window across the night.': 'Gece yarısını aşıyor — geceyi tek aralık olarak kapsar.',
  'Clock time + sun phase — sorts by time of day across all dates': 'Saat + güneş evresi — tüm tarihler boyunca günün saatine göre sıralar',
  'Minimum duration (minutes)': 'En kısa süre (dakika)',
  'Maximum duration (minutes)': 'En uzun süre (dakika)',
  'Minimum distance (km)': 'En kısa mesafe (km)',
  'Maximum distance (km)': 'En uzun mesafe (km)',
  'Minimum altitude (m)': 'En düşük irtifa (m)',
  'Maximum altitude (m)': 'En yüksek irtifa (m)',
  histogram: 'histogramı',
  vs: '/',
  'drag to seek, hover to read': 'sürükleyerek atla, üzerine gelerek oku',
  'drag to orbit': 'döndürmek için sürükle',
  reset: 'sıfırla',
  shadows: 'gölgeler',
  highlights: 'parlak alanlar',
  'No telemetry flights loaded yet. Add an .SRT or a folder.': 'Henüz telemetri uçuşu yüklenmedi. Bir .SRT ya da klasör ekleyin.',
  '⊞ Data': '⊞ Veri',
  'Telemetry data table': 'Telemetri veri tablosu',
  'When this flight was recorded (from the SRT)': 'Bu uçuşun kayıt zamanı (SRT’den)',

  // Stage modes / controls
  Video: 'Video',
  Split: 'Bölünmüş',
  Map: 'Harita',
  'Stage layout': 'Sahne düzeni',
  'Play/pause': 'Oynat/duraklat',
  Play: 'Oynat',
  Pause: 'Duraklat',
  'Keyboard shortcuts': 'Klavye kısayolları',
  'Keyboard shortcuts (?)': 'Klavye kısayolları (?)',
  'Play / pause': 'Oynat / duraklat',
  'Back / forward 5s': 'Geri / ileri 5sn',
  'Toggle map': 'Haritayı aç/kapat',
  'Merge / split charts': 'Grafikleri birleştir / ayır',
  'This help': 'Bu yardım',
  'Close popovers': 'Açılır pencereleri kapat',
  'Playback speed': 'Oynatma hızı',
  'Swap sides': 'Tarafları değiştir',
  'Drag to move · click to enlarge': 'Taşımak için sürükle · büyütmek için tıkla',
  Resize: 'Yeniden boyutlandır',
  'Replay mode — no video': 'Tekrar modu — video yok',
  'Loading 3D scene…': '3B sahne yükleniyor…',

  // Timeline
  'Timeline charts': 'Zaman çizelgesi grafikleri',
  'Choose what the timeline graphs': 'Zaman çizelgesinin çizeceği verileri seçin',
  charts: 'grafik',
  'Add chart': 'Grafik ekle',
  'Merge all into one chart ([)': 'Hepsini tek grafikte birleştir ([)',
  'Merge all charts': 'Tüm grafikleri birleştir',
  'Split into separate charts (])': 'Ayrı grafiklere böl (])',
  'Split all charts': 'Tüm grafikleri ayır',
  'Remove this chart': 'Bu grafiği kaldır',
  'Drag to reorder': 'Sıralamak için sürükle',
  'Drag to move this pane': 'Panoyu taşımak için sürükle',
  'Close pane': 'Panoyu kapat',
  'Global panes': 'Global panolar',
  Back: 'Geri',
  'Chart series': 'Grafik serileri',
  'Pick a metric': 'Bir metrik seç',
  Table: 'Tablo',
  'Records the current on-screen layout (video, map and widgets, exactly as arranged) into a video clip.': 'Ekrandaki mevcut düzeni (video, harita ve göstergeler, düzenlendiği gibi) bir video klibe kaydeder.',
  Common: 'Ortak',
  'Hide title': 'Başlığı gizle',
  'This widget': 'Bu widget',
  'Area fill': 'Alan dolgusu',
  'Reference lines': 'Referans çizgileri',
  'Zero-based axis': 'Sıfır tabanlı eksen',
  Redline: 'Kırmızı çizgi',
  'Full scale': 'Tam skala',
  auto: 'otomatik',
  Bins: 'Kutu sayısı',
  'Point size': 'Nokta boyutu',
  'Trend line': 'Trend çizgisi',
  Smoothing: 'Yumuşatma',
  'Line width': 'Çizgi kalınlığı',
  'Apply to all charts': 'Tüm grafiklere uygula',
  Range: 'Menzil',
  Caution: 'Dikkat',
  'Min speed': 'Min hız',
  Sensitivity: 'Hassasiyet',
  Decimals: 'Ondalık',
  'Altitude scale': 'İrtifa abartısı',
  'Y range': 'Y aralığı',
  'Reset to defaults': 'Varsayılana sıfırla',
  'Custom colour': 'Özel renk',

  // LUT
  'LUT on': 'LUT açık',
  'LUT off': 'LUT kapalı',

  // Sidebar / library
  Add: 'Ekle',
  'Add a video, an': 'Bir video, bir',
  ', or a whole folder — same-named files pair automatically.':
    ' ya da bütün bir klasör ekle — aynı adlı dosyalar otomatik eşleşir.',
  Flights: 'Uçuşlar',
  'Show flights': 'Uçuşları göster',
  'Hide panel': 'Paneli gizle',
  'Flights workspace — compare all flights': 'Uçuş çalışma alanı — tüm uçuşları karşılaştır',
  "All flights map — where you've flown": 'Tüm uçuşlar haritası — nerede uçtun',
  'Flights workspace': 'Uçuş çalışma alanı',
  'Resize flights panel': 'Uçuş panelini yeniden boyutlandır',
  'Drop files here, or use': 'Dosyaları buraya bırak veya kullan',
  'video only — no telemetry': 'yalnızca video — telemetri yok',
  'indexing…': 'dizinleniyor…',
  'Max altitude': 'Maks irtifa',
  'Max distance from home': 'Eve maks mesafe',
  'Top speed': 'Tepe hız',
  'Subtitle data — drag off to detach': 'Altyazı verisi — ayırmak için sürükle',
  'Reopen folder': 'Klasörü tekrar aç',
  'Add folder': 'Klasör ekle',
  'Add video / SRT': 'Video / SRT ekle',
  'Recent folders': 'Son klasörler',
  'scanning…': 'taranıyor…',
  'Review your drone footage in sync with its telemetry.': 'Drone çekimlerinizi telemetriyle senkron inceleyin.',
  'Add a video with its same-named DJI .SRT — they pair automatically.': 'Bir videoyu aynı adlı DJI .SRT dosyasıyla ekleyin — kendiliğinden eşleşirler.',
  'The map, instrument widgets and timeline charts all play with the flight.': 'Harita, gösterge parçaları ve zaman çizelgesi grafikleri uçuşla birlikte oynar.',
  'Drop a whole folder to import many flights at once.': 'Tüm bir klasörü bırakarak birçok uçuşu bir kerede içe aktarın.',
  'Export any on-screen layout as a video clip.': 'Ekrandaki herhangi bir düzeni video klip olarak dışa aktarın.',
  'Drag onto a video to pair': 'Eşlemek için bir videonun üzerine sürükle',
  Remove: 'Kaldır',
  'On map overlay': 'Harita bindirmesinde',
  'Overlay on map': 'Haritaya bindir',
  Date: 'Tarih',
  Duration: 'Süre',
  'Max alt': 'Maks irtifa',
  'Max dist': 'Maks mesafe',
  Path: 'İz',
  'Top spd': 'Tepe hız',
  'Avg spd': 'Ort hız',
  Frames: 'Kare',

  // Add button
  '🎞 Video / SRT files…': '🎞 Video / SRT dosyaları…',
  '📂 Folder (auto-pair)…': '📂 Klasör (otomatik eşle)…',
  '…or drag files / folders onto the panel': '…veya dosya / klasörleri panele sürükle',
  'Klasör seçici desteklenmiyor — klasörü panele sürükleyip bırakın.': 'Klasör seçici desteklenmiyor — klasörü panele sürükleyip bırakın.',

  // Dock / catalog
  'Catalog — click': 'Katalog — tıkla',
  'to add to the view, or drag it to place': 'görünüme eklemek için, ya da yerleştirmek için sürükle',
  'Search widgets…': 'Widget ara…',
  'Show data panel': 'Veri panelini göster',
  'Hide data panel': 'Veri panelini gizle',
  '▾ Hide': '▾ Gizle',
  '▴ Data': '▴ Veri',
  '⛶ Full': '⛶ Tam',
  '✕ Exit': '✕ Çık',
  'Expand to fill — see all widgets': 'Tüm alanı kapla — tüm widget’ları gör',
  'Restore the view': 'Görünümü geri yükle',
  Flight: 'Uçuş',
  Position: 'Konum',
  Camera: 'Kamera',
  Motion: 'Hareket',
  Attitude: 'Duruş',
  Signal: 'Sinyal',
  Other: 'Diğer',
  'Constant for the whole flight': 'Uçuş boyunca sabit',
  Charts: 'Grafikler',
  Distribution: 'Dağılım',
  Relation: 'İlişki',
  '3D': '3B',

  'Show timeline graph': 'Zaman çizelgesi grafiğini göster',
  'Hide timeline graph': 'Zaman çizelgesi grafiğini gizle',
  'Fullscreen video (double-click video, or F)': 'Tam ekran video (videoya çift tıkla veya F)',
  'Fullscreen (F, or double-click the video)': 'Tam ekran (F veya videoya çift tıkla)',
  'Exit fullscreen': 'Tam ekrandan çık',
  'Reset zoom': 'Yakınlaştırmayı sıfırla',
  Seek: 'İlerle/geri sar',
  'Colour adjustment': 'Renk ayarı',
  Sticks: 'Kumanda çubukları',
  'Stick input': 'Kumanda girişi',
  'Estimated Mode-2 control inputs from the drone’s motion (uses real aircraft attitude when a flight log is loaded).':
    'Drone’un hareketinden tahmin edilen Mod-2 kumanda girişleri (uçuş günlüğü yüklüyse gerçek uçak duruşu kullanılır).',
  'Camera tilt (pitch) + roll from the DJI flight log.': 'DJI uçuş günlüğünden kamera eğimi (pitch) + roll.',
  flights: 'uçuş',
  Background: 'Arka plan',
  Framed: 'Çerçeveli',
  Glass: 'Cam',
  None: 'Yok',
  Exposure: 'Pozlama',
  Brightness: 'Parlaklık',
  Contrast: 'Kontrast',
  Saturation: 'Doygunluk',
  Temperature: 'Sıcaklık',
  Shadows: 'Gölgeler',

  Preferences: 'Tercihler',

  // DJI flight-log import
  'Flight log': 'Uçuş günlüğü',
  'DJI flight-log import': 'DJI uçuş günlüğü içe aktarma',
  'DJI developer API key': 'DJI geliştirici API anahtarı',
  'paste your SDK key': 'SDK anahtarını yapıştır',
  'When this flight was recorded': 'Bu uçuşun kaydedildiği an',
  'Rich flight data from a DJI flight log (heading + gimbal)': 'DJI uçuş günlüğünden zengin uçuş verisi (yön + gimbal)',
  'Decoding flight log…': 'Uçuş günlüğü çözülüyor…',
  Clear: 'Temizle',
  Copy: 'Kopyala',
  Copied: 'Kopyalandı',
  'Decrypts your logs on this machine. Stored only in this browser and sent only to the app’s local decoder — flight data never leaves your machine.':
    'Günlüklerinizi bu makinede çözer. Yalnız bu tarayıcıda saklanır ve yalnız uygulamanın yerel çözücüsüne gönderilir — uçuş verisi makinenizden çıkmaz.',
  'How to get it (once):': 'Nasıl alınır (bir kez):',
  'Sign in at DJI Developer with your DJI account.': 'DJI hesabınızla DJI Developer’a giriş yapın.',
  'Create App → choose “Open API”.': 'Create App → “Open API” seçin.',
  'Activate it via the emailed link.': 'E-postayla gelen bağlantıyla etkinleştirin.',
  'Copy the app’s “SDK key” into the field above.': 'Uygulamanın “SDK key” değerini yukarıdaki alana yapıştırın.',
  'Open DJI Developer': 'DJI Developer’ı aç',
  'Where are the flight logs?': 'Uçuş günlükleri nerede?',
  'On the RC2 (or phone), logs are .txt files here:': 'RC2’de (veya telefonda) günlükler burada .txt dosyalarıdır:',
  'Connect the controller by USB, then drag that folder onto the app (or use ＋). Re-open it after new flights — only new logs are added.':
    'Kumandayı USB ile bağlayın, sonra o klasörü uygulamaya sürükleyin (veya ＋ kullanın). Yeni uçuşlardan sonra tekrar açın — yalnız yeni günlükler eklenir.',
  'Transfer from the RC2 over USB (Mac)': 'RC2’den USB ile aktarım (Mac)',
  'The RC2 shows up as an MTP device, not a normal drive. On macOS, open it with Android File Transfer, copy the FlightRecord folder to your computer, then drag it here.':
    'RC2, normal bir sürücü değil MTP aygıtı olarak görünür. macOS’ta Android File Transfer ile açın, FlightRecord klasörünü bilgisayarınıza kopyalayın, sonra buraya sürükleyin.',
  'Power user: if USB debugging is on in the controller’s developer options, pull the logs with adb:':
    'İleri kullanım: Kumandanın geliştirici seçeneklerinde USB hata ayıklama açıksa, kayıtları adb ile çekebilirsiniz:',
  'adb isn’t enabled on every DJI controller — if it fails or lists no device, use the Android File Transfer route above.':
    'adb her DJI kumandasında etkin değildir — çalışmazsa ya da aygıt listelenmezse yukarıdaki Android File Transfer yolunu kullanın.',
  'How to copy DJI logs': 'DJI günlükleri nasıl kopyalanır',
  'DJI flight log — richer, standalone flight data': 'DJI uçuş günlüğü — daha zengin, bağımsız uçuş verisi',
  'DJI flight log — heading + gimbal, opens on click': 'DJI uçuş günlüğü — yön + gimbal, tıklayınca açılır',
  'Add your DJI developer API key in Settings to decode flight logs.': 'Uçuş günlüklerini çözmek için Ayarlar’dan DJI geliştirici API anahtarınızı ekleyin.',

  // Map overlay options & legend
  Time: 'Zaman',
  Track: 'İz',
  Width: 'Kalınlık',
  Events: 'Olaylar',
  Markers: 'İşaretler',
  'V/S': 'D/H',
  Dist: 'Mesafe',
  'Show event pins': 'Olay işaretlerini göster',
  'Hide event pins': 'Olay işaretlerini gizle',
  'Video settings': 'Video ayarları',
  'Map settings': 'Harita ayarları',
  View: 'Görünüm',
  Overlays: 'Yer işaretleri',
  Layers: 'Katmanlar',
  '3D buildings': '3B binalar',
  'Track width': 'İz kalınlığı',
  Buildings: 'Binalar',
  'Auto-follow': 'Otomatik takip',
  'Go to current': 'Anlık konuma git',
  'Reset view': 'Görünümü sıfırla',
  'Open the map to use this': 'Bunu kullanmak için haritayı açın',
  '3D — path at real altitude over terrain': '3B — iz, arazi üzerinde gerçek irtifada',
  'Elapsed time': 'Geçen süre',
  'Ground speed (m/s)': 'Yatay hız (m/s)',
  'Height above takeoff': 'Kalkışa göre yükseklik',
  'Vertical speed — climb/descent rate': 'Düşey hız — tırmanma/alçalma oranı',
  'Elapsed flight time': 'Geçen uçuş süresi',
  'Solid colour, no metric': 'Düz renk, ölçüt yok',
  'GPS-loss chips': 'GPS-kaybı etiketleri',
  'Hover cursor': 'Gezinme imleci',
  'White casing (glow)': 'Beyaz kılıf (parıltı)',
  '3D altitude curtain': '3B irtifa perdesi',
  'Camera view cone (approx.)': 'Kamera görüş konisi (yaklaşık)',
  'Camera view cone': 'Kamera görüş konisi',
  'Video on map (approx.)': 'Haritada video (yaklaşık)',
  'Video on map': 'Haritada video',
  'Direction from the flight log’s aircraft heading': 'Yön, uçuş günlüğündeki uçak yönünden',
  'Uses the log’s heading + gimbal pitch for a true ground footprint': 'Gerçek yer izdüşümü için günlüğün yön + gimbal eğimini kullanır',
  Opacity: 'Saydamlık',
  'No gimbal data in the clip — uses flight heading, exact only in forward flight': 'Klipte gimbal verisi yok — uçuş yönünü kullanır, yalnız ileri uçuşta birebir',
  'Projects the current video frame onto its approximate ground footprint (no gimbal data)': 'Geçerli video karesini yaklaşık yer izdüşümüne yansıtır (gimbal verisi yok)',
  '3D buildings (OpenStreetMap)': '3B binalar (OpenStreetMap)',
  'Overlay options': 'Bindirme seçenekleri',
  'Recording start': 'Kayıt başlangıcı',
  'Recording stop': 'Kayıt bitişi',
  'Hide widgets': 'Parçaları gizle',
  'Show widgets': 'Parçaları göster',
  'Drag to resize width': 'Genişliği ayarlamak için sürükle',
  'Drag to resize height': 'Yüksekliği ayarlamak için sürükle',
  'Drag the widget edges to resize': 'Boyutlandırmak için kenarlarından sürükle',
  'Reset zoom (double-click)': 'Yakınlaştırmayı sıfırla (çift tıkla)',
  'Drag to dock elsewhere': 'Başka yere yerleştirmek için sürükle',
  'Drag to move · click to dock': 'Taşımak için sürükle · yerleştirmek için tıkla',
  'Drag to move · click to collapse': 'Taşımak için sürükle · kapatmak için tıkla',
  'Collapse controls': 'Denetimleri kapat',
  'Map controls (drag to move)': 'Harita denetimleri (taşımak için sürükle)',
  'Show map controls': 'Harita denetimlerini göster',

  // VizCard
  Fullscreen: 'Tam ekran',
  'Click to add to the view · drag to place it': 'Görünüme eklemek için tıkla · yerleştirmek için sürükle',
  'On the view — click to remove · drag to place elsewhere': 'Görünümde — kaldırmak için tıkla · başka yere sürükle',

  // Presets
  'Layout presets': 'Düzen presetleri',
  '🛩 Flight overview': '🛩 Uçuş genel görünüm',
  '🎮 RC2 commander': '🎮 RC2 komuta',
  '💨 Speed & altitude': '💨 Hız & irtifa',
  '🧭 Position & nav': '🧭 Konum & navigasyon',
  '📷 Camera & exposure': '📷 Kamera & pozlama',
  '🚫 Clean (no widgets)': '🚫 Temiz (widget yok)',
  '✎ Custom': '✎ Özel',
  '• edited': '• düzenlendi',
  'Saved layouts': 'Kayıtlı düzenler',
  'Update this layout with current edits': 'Bu düzeni mevcut düzenlemelerle güncelle',
  Delete: 'Sil',
  '↩ Previous layout': '↩ Önceki düzen',
  '💾 Save current layout as…': '💾 Mevcut düzeni farklı kaydet…',
  'Layout name…': 'Düzen adı…',
  Save: 'Kaydet',
  'Export settings': 'Dışa aktarma ayarları',
  'Export video': 'Videoyu dışa aktar',
  Resolution: 'Çözünürlük',
  'Frame rate': 'Kare hızı',
  Source: 'Kaynak',
  Audio: 'Ses',
  Included: 'Dahil',
  Muted: 'Sessiz',
  Format: 'Biçim',
  Length: 'Süre',
  'Est. size': 'Tahmini boyut',
  'Recorded in real time — it takes about as long as the clip.': 'Gerçek zamanlı kaydedilir — klip kadar sürer.',
  'Export mode': 'Dışa aktarma modu',
  'Screen recording': 'Ekran kaydı',
  'The exact layout, in real time': 'Tam düzen, gerçek zamanlı',
  'Analysis clip': 'Analiz klibi',
  'Flight path + telemetry — fast, MP4': 'Uçuş izi + telemetri — hızlı, MP4',
  'Needs a flight with GPS': 'GPS içeren bir uçuş gerekir',
  'An offline flight-path animation coloured by the map metric, with a live telemetry readout. No source video.':
    'Harita ölçütüne göre renklendirilmiş, canlı telemetri okumalı çevrimdışı uçuş-izi animasyonu. Kaynak video yok.',
  'Rendered offline — faster than the clip length.': 'Çevrimdışı işlenir — klip süresinden daha hızlı.',
  'Rendering analysis clip…': 'Analiz klibi işleniyor…',
  'Rendering offline — this is quick.': 'Çevrimdışı işleniyor — bu hızlıdır.',
  'Full composite': 'Tam bileşik',
  'Video + HUD, offline & fast (MP4)': 'Video + HUD, çevrimdışı & hızlı (MP4)',
  'Needs an MP4/MOV source': 'MP4/MOV kaynağı gerekir',
  'Decoded from the original file, offline and faster than real time — the source audio is kept.':
    'Özgün dosyadan çözülür, çevrimdışı ve gerçek zamandan hızlı — kaynak ses korunur.',
  'Rendered offline — faster than real time.': 'Çevrimdışı işlenir — gerçek zamandan hızlı.',
  'Rendering offline…': 'Çevrimdışı işleniyor…',
  'Rendering offline — faster than real time.': 'Çevrimdışı işleniyor — gerçek zamandan hızlı.',
  'Export failed': 'Dışa aktarma başarısız',
  Dismiss: 'Kapat',
  'The whole layout is captured except these controls — widget positions and colour adjustments are preserved.':
    'Bu kontroller dışında tüm düzen yakalanır — widget konumları ve renk ayarları korunur.',
  'The 3D path and colour-histogram widgets don’t appear in the export yet.':
    '3B iz ve renk-histogramı widget’ları çıktıda henüz görünmüyor.',
  'Key stored on this machine': 'Anahtar bu makinede saklı',
  'enter a new key to replace': 'değiştirmek için yeni anahtar girin',
  'Stored on this machine in a private file (not in the browser) and used only by the app’s local decoder — your key and flight data never leave your computer.':
    'Bu makinede özel bir dosyada saklanır (tarayıcıda değil) ve yalnızca uygulamanın yerel çözücüsü kullanır — anahtarınız ve uçuş verileriniz bilgisayarınızdan çıkmaz.',

  // Workspace
  indexed: 'dizinlendi',
  'Filter by name…': 'Ada göre süz…',
  'Close (Esc)': 'Kapat (Esc)',
  'Total time': 'Toplam süre',
  'Total distance': 'Toplam mesafe',
  'Peak altitude': 'Tepe irtifa',
  When: 'Ne zaman',
  'Shape compare': 'Şekil karşılaştırma',
  'Home-aligned paths — compare shape & size regardless of where each was flown.':
    'Eve hizalı izler — nerede uçulduğundan bağımsız şekil & boyut karşılaştırması.',
  "Click a row's name to open it · ⧉ adds it to the shape compare above (and the map overlay)":
    'Bir satırın adına tıkla → aç · ⧉ üstteki şekil karşılaştırmasına (ve harita bindirmesine) ekler',

  // Data table
  'Telemetry data': 'Telemetri verisi',
  rows: 'satır',
  channels: 'kanal',
  time: 'zaman',
  derived: 'türetilmiş',
  Filter: 'Süz',
  'Filter channel': 'Süzme kanalı',
  Every: 'Her',
  row: 'satır',
  Heatmap: 'Isı haritası',
  'Reset sort': 'Sıralamayı sıfırla',
  '⌖ Current row': '⌖ Mevcut satır',

  // All flights map
  'All flights': 'Tüm uçuşlar',
  'click a track to open it': 'açmak için bir ize tıkla',
  'flights with GPS': 'GPS’li uçuş',
  'No GPS flights loaded yet.': 'Henüz GPS’li uçuş yüklenmedi.',

  // Flight planning
  'Flight planning': 'Uçuş planlama',
  'Click the map to check a launch point': 'Kalkış noktasını denetlemek için haritaya tıklayın',
  'Click anywhere on the map to assess a launch point: airspace zones, temporary restrictions (NOTAM), and current weather.':
    'Bir kalkış noktasını değerlendirmek için haritada herhangi bir yere tıklayın: hava sahası bölgeleri, geçici kısıtlamalar (NOTAM) ve güncel hava durumu.',
  'Checking zones and weather…': 'Bölgeler ve hava durumu denetleniyor…',
  'Could not assess this point': 'Bu nokta değerlendirilemedi',
  'Weather is unavailable for this location.': 'Bu konum için hava durumu alınamadı.',
  'Weather is rate-limited right now; try again shortly.': 'Hava durumu şu an hız sınırında; birazdan tekrar deneyin.',
  'Suitable to fly': 'Uçuşa uygun',
  'Marginal — caution': 'Sınırda — dikkatli olun',
  'Do not fly': 'Uçmayın',
  'No-fly zones here': 'Buradaki uçuşa yasak bölgeler',
  'Active restrictions (NOTAM)': 'Yürürlükteki kısıtlamalar (NOTAM)',
  'No-fly (red)': 'Uçuşa yasak (kırmızı)',
  // Short forms for the always-on map key, where the swatch itself carries the colour and the
  // texture, so repeating "(kırmızı)" in the label would only cost width on a phone.
  'No-fly': 'Uçuşa yasak',
  Allowed: 'İzinli',
  'Allowed (green)': 'İzinli (yeşil)',
  'Restrictions (NOTAM)': 'Kısıtlamalar (NOTAM)',
  'Plan from here': 'Buradan planla',
  'Check this point': 'Bu noktayı denetle',
  'Indexing zones…': 'Bölgeler indeksleniyor…',
  'Weather layer': 'Hava katmanı',
  Temp: 'Sıcaklık',
  Rain: 'Yağış',
  '3D terrain & buildings': '3B arazi ve binalar',
  'more here': 'daha burada',
  Reason: 'Neden',
  Type: 'Tür',
  Code: 'Kod',
  Meaning: 'Anlamı',
  Valid: 'Geçerlilik',
  surface: 'Yer',
  unlimited: 'Sınırsız',
  permanent: 'Kalıcı',
  'open-ended': 'Süresiz',
  'Zones here': 'Buradaki bölgeler',
  'No airspace zone at this point.': 'Bu noktada hava sahası bölgesi yok.',
  'Effective zone (merge overlaps)': 'Etkin bölge (örtüşenleri birleştir)',
  'Wind unit': 'Rüzgâr birimi',
  'Elevation colors': 'Yükseklik renkleri',
  'Hillshade relief': 'Kabartma gölgeleme',
  'Contour lines': 'Eş yükselti eğrileri',
  'Map — press Enter to assess the centre point': 'Harita — merkez noktayı değerlendirmek için Enter’a basın',
  'Airports & military': 'Havaalanı ve askeri',
  'My flights': 'Uçuşlarım',
  'My flight': 'Uçuşum',
  Authority: 'Yetkili kurum',
  'NOTAM types': 'NOTAM tipleri',
  Undefined: 'Tanımsız',
  'Close flight planning': 'Uçuş planlamayı kapat',
  'Updating…': 'Güncelleniyor…',
  Updated: 'Güncellendi',
  'Zone data unavailable': 'Bölge verisi alınamadı',
  'No-fly zone data could not be loaded — this map is NOT reliable right now. Do not use it to decide where to fly.':
    'Uçuşa yasak bölge verisi yüklenemedi — bu harita şu an GÜVENİLİR DEĞİL. Nereye uçacağınıza karar vermek için kullanmayın.',
  'The map could not be started in this browser. This page cannot tell you where you may fly — check İHATTYS instead.':
    'Harita bu tarayıcıda başlatılamadı. Bu sayfa nereye uçabileceğinizi söyleyemez — İHATTYS üzerinden bakın.',
  'Temporary restrictions (NOTAM) could not be loaded, so they are not shown. The permanent zones below are current.':
    'Geçici kısıtlamalar (NOTAM) yüklenemedi, bu yüzden gösterilmiyor. Aşağıdaki kalıcı bölgeler güncel.',
  'Merged view unavailable — showing the raw zones instead.':
    'Birleştirilmiş görünüm kullanılamıyor — bölgeler ham hâliyle gösteriliyor.',
  'Checking weather…': 'Hava durumu denetleniyor…',
  'Zoom in to load zones': 'Bölgeleri yüklemek için yakınlaştırın',
  'Search a place…': 'Yer ara…',
  'Search a place': 'Yer ara',
  'Search results': 'Arama sonuçları',
  'Layers & filters': 'Katmanlar ve süzgeçler',
  'View mode': 'Görünüm',
  'SHGM zones': 'SHGM bölgeleri',
  'Reduced map (WebGL unavailable) — 3D, terrain and place-name level filtering are off; everything else works.': 'Sadeleştirilmiş harita (WebGL yok) — 3B, arazi ve yer-adı seviye filtresi kapalı; gerisi çalışıyor.',
  Categories: 'Kategoriler',
  'Refresh data': 'Verileri yenile',
  'Start typing to search flights, zones and places': 'Uçuşlarda, bölgelerde ve yerlerde aramak için yazmaya başlayın',
  'Click: toggle · Cmd/Ctrl-click: isolate': 'Tıkla: aç/kapat · Cmd/Ctrl-tıkla: yalnızca bunu göster',
  'Weather layers': 'Hava durumu katmanları',
  Forecast: 'Tahmin',
  'Forecast time': 'Tahmin zamanı',
  Now: 'Şimdi',
  h: 'sa',
  'Why restricted': 'Neden kısıtlı',
  Category: 'Kategori',
  All: 'Tümü',
  'Airfield symbols': 'Havaalanı sembolleri',
  'Site symbols': 'Önemli yer sembolleri',
  Symbols: 'Semboller',
  Names: 'İsimler',
  'Place names': 'Yer isimleri',
  Provinces: 'İl',
  Districts: 'İlçe',
  Villages: 'Köy',
  clips: 'klip',
  'max alt': 'en yüksek',
  legs: 'bacak',
  'Frame previews': 'Kare önizlemeleri',
  'No video file found next to this clip.': 'Bu klibin yanında video dosyası bulunamadı.',
  'Preparing…': 'Hazırlanıyor…',
  'Preparing storyboard…': 'Kareler hazırlanıyor…',
  'Preparing storyboards…': 'Storyboard’lar hazırlanıyor…',
  'Storyboards ready': 'Storyboard’lar hazır',
  'Preview unavailable — the video duration could not be read.':
    'Önizleme yok — video süresi okunamadı.',
  'Preview unavailable — frames could not be extracted.':
    'Önizleme yok — kareler çıkarılamadı.',
  'Marked site': 'İşaretli yer',
  'Military airfield': 'Askeri havaalanı',
  Heliport: 'Heliport',
  Airport: 'Havaalanı',
  'click for detail': 'ayrıntı için tıklayın',
  'Airborne route (3D)': 'Havadaki rota (3B)',
  'Distance from launch': 'Kalkış noktasına uzaklık',
  Storyboard: 'Storyboard',
  'Farthest point': 'En uzak nokta',
  Sources: 'Kaynaklar',
  'Folder & file': 'Klasör ve dosya',
  Folder: 'Klasör',
  File: 'Dosya',
  Clip: 'Klip',
  Description: 'Açıklama',
  'Date/time': 'Tarih/saat',
  'SRT telemetry': 'SRT telemetri',
  'GPS points': 'GPS noktası',
  'Video metadata': 'Video meta verisi',
  Codec: 'Kodek',
  Bitrate: 'Bit hızı',
  'Colour profile': 'Renk profili',
  Detected: 'Algılanan',
  'from file name': 'dosya adından',
  'Profile inferred from the file name': 'Profil dosya adından çıkarıldı',
  Preview: 'Önizleme',
  // Timeline / multi-clip / export controls — were rendering in English under the Turkish UI.
  'Dock into the grid': 'Izgaraya yerleştir',
  Hide: 'Gizle',
  'Hide (bring it back from the bar)': 'Gizle (çubuktan geri getir)',
  'No clip at this time': 'Bu anda klip yok',
  'Resize charts': 'Grafikleri yeniden boyutlandır',
  'Drag to resize · drag down to close': 'Boyutlandırmak için sürükle · kapatmak için aşağı sürükle',
  'Add video(s)': 'Video ekle',
  Offset: 'Kaydırma',
  'Start this clip at the playhead': 'Bu klibi oynatma başına al',
  'to playhead': 'oynatma başına',
  'Click a clip to set its exact sync offset': 'Klibin tam eşitleme kaydırmasını ayarlamak için tıkla',
  'Add clip(s) to this track': 'Bu parçaya klip ekle',
  'Add clips': 'Klip ekle',
  'Remove this video': 'Bu videoyu kaldır',
  'Stage sources': 'Sahne kaynakları',
  'Add synced video': 'Eşitlenmiş video ekle',
  Reset: 'Sıfırla',
  Gimbal: 'Gimbal',
  'The map can’t start': 'Harita başlatılamıyor',
  'Loading…': 'Yükleniyor…',
  'Reduced map (WebGL unavailable) — basemap, no-fly zones and flights only; no 3D / weather / vector labels.':
    'Sadeleştirilmiş harita (WebGL yok) — yalnız basemap, yasak bölgeler ve uçuşlar; 3B / hava durumu / vektör etiket yok.',
  'Your browser couldn’t initialize WebGL, which the map needs to render. This is a browser/GPU setting — not the flight data or the app.':
    'Tarayıcınız haritanın çizim için gereken WebGL’i başlatamadı. Bu bir tarayıcı/GPU ayarı — uçuş verisi ya da uygulama değil.',
  'Fix: turn on “Use graphics acceleration when available” in your browser’s settings and relaunch it; open the browser’s GPU status page to see why it’s off; or update your graphics driver.':
    'Çözüm: tarayıcı ayarlarından “Uygun olduğunda donanım hızlandırmasını kullan” seçeneğini açıp tarayıcıyı yeniden başlatın; kapalıysa nedenini görmek için tarayıcının GPU durum sayfasını açın; ya da ekran kartı sürücünüzü güncelleyin.',
  // Actionable export-error copy (were showing raw English exception text).
  'This clip’s codec can’t be exported in the browser. Re-encode it to H.264 MP4 and try again.':
    'Bu klibin kodeki tarayıcıda dışa aktarılamıyor. H.264 MP4’e yeniden kodlayıp tekrar deneyin.',
  'Ran out of memory during export. Try a lower resolution or a shorter range.':
    'Dışa aktarma sırasında bellek yetmedi. Daha düşük çözünürlük ya da daha kısa aralık deneyin.',
  'Couldn’t read the source media. Re-add the file and try again.':
    'Kaynak medya okunamadı. Dosyayı yeniden ekleyip tekrar deneyin.',
  'Export failed. Try a lower resolution; details are in the browser console.':
    'Dışa aktarma başarısız. Daha düşük çözünürlük deneyin; ayrıntılar tarayıcı konsolunda.',
  'Ground projection': 'Zemine izdüşüm',
  'Zone display': 'Bölge gösterimi',
  'Effective = overlapping zones merged; the most specific (smallest) at each point wins.':
    'Birleşik = çakışan bölgeler birleştirilir; her noktada en özgül (en küçük) olan kazanır.',
  Direct: 'Ham',
  Effective: 'Birleşik',
  Zones: 'Bölgeler',
  Terrain: 'Arazi',
  'Map data': 'Harita verileri',
  'Search the map…': 'Haritada ara…',
  'Search everything on the map': 'Haritadaki her şeyde ara',
  'Search in': 'Şurada ara',
  'Filter by category': 'Kategoriye göre süz',
  'Click: toggle · Cmd/Ctrl-click: toggle the rest': 'Tık: aç/kapa · Cmd/Ctrl-tık: diğerlerini aç/kapa',
  'Layers & filters (f)': 'Katmanlar ve süzgeçler (f)',
  'Focus search': 'Aramaya odaklan',
  'Rotate / tilt (2D↔3D)': 'Döndür / eğ (2B↔3B)',
  'Isolate a filter pill (toggle the rest)': 'Bir süzgeç çipini yalnızla (diğerlerini ters çevir)',
  'Move through results': 'Sonuçlarda gezin',
  'Go to highlighted result': 'Seçili sonuca git',
  'Search scope': 'Arama kapsamı',
  'SHGM airspace': 'SHGM sahaları',
  'Flights & places': 'Uçuş & yer',
  'Narrow down': 'Daralt',
  'Zone type': 'Bölge türü',
  'NOTAM type': 'NOTAM türü',
  'Map legend': 'Harita göstergesi',
  'Place search is unavailable right now — check your connection.': 'Yer araması şu an kullanılamıyor — bağlantınızı denetleyin.',
  drag: 'sürükle',
  click: 'tıkla',
  Places: 'Yerler',
  'Start typing, or pick a category above': 'Yazmaya başlayın ya da yukarıdan bir kategori seçin',
  'No matches': 'Eşleşme yok',
  'No data yet': 'Henüz veri yok',
  'refine search to see more': 'daha fazlası için aramayı daraltın',
  // Background-activity console.
  'Scanning archive…': 'Arşiv taranıyor…',
  'Scan complete': 'Tarama bitti',
  'Working…': 'Çalışıyor…',
  'Background activity': 'Arka plan işlemleri',
  'Loading airspace zones': 'Hava sahası bölgeleri yükleniyor',
  'Scanning flights': 'Uçuşlar taranıyor',
  'Loading weather': 'Hava durumu yükleniyor',
  Weather: 'Hava durumu',
  Gust: 'Ani rüzgâr hızı',
  Precipitation: 'Yağış',
  gust: 'ani',
  Calm: 'sakin',
  Risky: 'riskli',
  Dangerous: 'tehlikeli',
  'Weather paused — daily request limit reached; showing cached data.':
    'Hava durumu duraklatıldı — günlük istek sınırına ulaşıldı; önbellekteki veri gösteriliyor.',
  '’den': '’den',
  mixed: 'karışık',
  'Sources & device': 'Kaynaklar ve cihaz',
  Device: 'Cihaz',
  Model: 'Model',
  Serial: 'Seri no',
  Firmware: 'Yazılım',
  'Flight controller': 'Uçuş kontrolcüsü',
  'Cross-source check': 'Kaynaklar arası denetim',
  Channel: 'Kanal',
  Kept: 'Tutulan',
  'Max divergence': 'En büyük sapma',
  exact: 'birebir',
  'Serial + GPS track + card path are jointly identifying — decide what leaves the tool before sharing.':
    'Seri no + GPS izi + kart yolu birlikte kimliklendirir — paylaşmadan önce dışarı ne çıkacağına karar verin.',
  'length & thickness = average; faded = gust': 'uzunluk & kalınlık = ortalama; soluk = ani',
  'more drops = heavier rain': 'daha çok damla = daha şiddetli yağış',
  Visibility: 'Görüş',
  'Advisory only. SHGM is the legal authority for Turkish airspace — verify at':
    'Yalnızca bilgilendirmedir. Türk hava sahasında yasal yetkili SHGM’dir — şu adresten doğrulayın:',

  // FlightMap controls + context menu
  'Colour the path by': 'İzi renklendir',
  Basemap: 'Altlık',
  Speed: 'Hız',
  Alt: 'İrtifa',
  Off: 'Kapalı',
  On: 'Açık',
  'Auto LUT': 'Otomatik LUT',
  Sat: 'Uydu',
  'Go to current position': 'Mevcut konuma git',
  'Auto-follow the drone': 'Drone’u otomatik takip et',
  '3D — path at real altitude over terrain (or just tilt the map)':
    '3B — iz gerçek irtifada arazi üstünde (ya da haritayı eğ)',
  'Reset north & tilt': 'Kuzeyi & eğimi sıfırla',
  '📋 Copy coordinates': '📋 Koordinatları kopyala',
  '✓ Copied': '✓ Kopyalandı',
  '⌖ Seek to nearest point here': '⌖ Buradaki en yakın noktaya atla',
  '◎ Centre map here': '◎ Haritayı buraya ortala',
  '↗ Open in Google Maps': '↗ Google Haritalar’da aç',
  'GPS signal lost — position unknown': 'GPS sinyali kayboldu — konum bilinmiyor',
  Collapse: 'Daralt',
  Expand: 'Genişlet',
  Dock: 'Yerine sabitle',
  'Dock as a pane': 'Pano olarak yerleştir',
  'Undock to overlay': 'Katmana geri al',
  'Pop out to a floating window': 'Yüzen pencereye çıkar',
  Restore: 'Geri al',
  'Drag to resize': 'Boyutlandırmak için sürükle',
  Float: 'Yüzdür',
  Widgets: 'Gösterge paneli',
  Export: 'Dışa aktar',
  'Export resolution': 'Dışa aktarma çözünürlüğü',
  'Burn the HUD onto the video and download a clip': 'Göstergeleri videoya gömüp klip indir',
  'Exporting video': 'Video dışa aktarılıyor',
  'Exporting overlay video…': 'Bindirme videosu dışa aktarılıyor…',
  'Recording in real time — keep this tab focused.': 'Gerçek zamanlı kaydediliyor — bu sekme açık kalsın.',
  Cancel: 'İptal',
  'GPS lost': 'GPS kaybı',
  Outage: 'Kesinti',
  'straight-line gap': 'düz çizgi mesafesi',
  Overlay: 'Bindirme',
  'fit all': 'hepsine sığdır',
  clear: 'temizle',

  // Widget titles + catalog labels
  Telemetry: 'Telemetri',
  Heading: 'Yön',
  'Speed gauge': 'Hız göstergesi',
  'Vertical speed': 'Dikey hız',
  Wind: 'Rüzgâr',
  'Wind (estimated)': 'Rüzgâr (tahmini)',
  'Altitude tape': 'İrtifa şeridi',
  Altitude: 'İrtifa',
  Distance: 'Mesafe',
  Compass: 'Pusula',
  'Flight path': 'Uçuş izi',
  'Distance radar': 'Mesafe radarı',
  Highlights: 'Öne çıkanlar',
  'Heading rose': 'Yön gülü',
  '3D flight path': '3B uçuş izi',
  'Speed histogram': 'Hız histogramı',
  'Altitude histogram': 'İrtifa histogramı',
  'Colour histogram': 'Renk histogramı',
  'Ground speed': 'Yer hızı',
  'Frame interval': 'Kare aralığı',
  Acceleration: 'İvme',
  'Turn rate': 'Dönüş oranı',
  'Climb angle': 'Tırmanış açısı',
  'Path length': 'İz uzunluğu',
  'Exposure (EV)': 'Pozlama (EV)',
  'Colour temp': 'Renk sıcaklığı',
  'Altitude vs Speed': 'İrtifa - Hız',
  'Altitude vs Distance': 'İrtifa - Mesafe',
  'from home': 'evden',
  distribution: 'dağılımı',
  'of flight': 'uçuşun',
  'Your browser confirms folder uploads and allows only one folder.': 'Tarayıcınız klasör yüklemesini onaylatıyor ve yalnızca tek klasöre izin veriyor.',
  'Tip: drag folders onto the panel — several at once, no confirmation.': 'İpucu: klasörleri panele sürükleyin — birden çok, onaysız.',
  'Load a video to see its colour histogram.': 'Renk histogramını görmek için video yükle.',

  // Events / highlights
  Takeoff: 'Kalkış',
  Landing: 'İniş',
  'Lowest point': 'En alçak nokta',
  'Fastest climb': 'En hızlı tırmanış',
  'Fastest descent': 'En hızlı iniş',
  'Farthest from home': 'Eve en uzak',
  'No GPS at launch': 'Kalkışta GPS yok',
  'GPS signal lost': 'GPS sinyali kayboldu',
  'No notable moments detected.': 'Belirgin an saptanmadı.',
  'Jump to': 'Şuraya atla:',

  // Channel labels (timeline selector, data table columns)
  'Relative altitude': 'Bağıl irtifa',
  'Absolute altitude': 'Mutlak irtifa',
  ISO: 'ISO',
  EV: 'EV',
  Aperture: 'Diyafram',
  'Focal length': 'Odak uzaklığı',
  'Color temp': 'Renk sıcaklığı',
  Latitude: 'Enlem',
  Longitude: 'Boylam',
  Course: 'Rota',
  'Distance from home': 'Eve mesafe',
  'East offset': 'Doğu kayması',
  'North offset': 'Kuzey kayması',
  'GPS fix': 'GPS kilidi',
  'Shutter (exposure)': 'Enstantane (pozlama)',
  'Color mode': 'Renk modu',

  // Wind widget
  est: 'tahmini',
  conf: 'güven',
  'need varied headings': 'çeşitli yön gerekli',
  from: 'yön',

  // Catalog: chart widget labels (registry uses "<name> chart")
  'No widgets match': 'Eşleşen widget yok',
  'Altitude chart': 'İrtifa grafiği',
  'Ground speed chart': 'Yer hızı grafiği',
  'Vertical speed chart': 'Dikey hız grafiği',
  'Acceleration chart': 'İvme grafiği',
  'Turn rate chart': 'Dönüş oranı grafiği',
  'Climb angle chart': 'Tırmanış açısı grafiği',
  'Distance chart': 'Mesafe grafiği',
  'Path length chart': 'İz uzunluğu grafiği',
  'Exposure (EV) chart': 'Pozlama (EV) grafiği',
  'Colour temp chart': 'Renk sıcaklığı grafiği',
  'Frame interval chart': 'Kare aralığı grafiği',

  // Catalog descriptions
  'Current ground speed on an arc gauge, with the trip max marked.':
    'Yay göstergede mevcut yer hızı; uçuşun en yükseği işaretli.',
  'Climb (+) or descent (−) rate right now.': 'Şu anki tırmanış (+) veya iniş (−) oranı.',
  'Estimated wind speed and direction, from how the drone holds position.':
    'Drone’un konumu nasıl tuttuğundan tahmin edilen rüzgâr hızı ve yönü.',
  'Altitude on a vertical tape, like a cockpit altimeter.': 'Dikey şeritte irtifa, kokpit altimetresi gibi.',
  'Key live numbers: altitude, distance, path length, coordinates.':
    'Önemli canlı değerler: irtifa, mesafe, iz uzunluğu, koordinatlar.',
  'Heading (direction of travel) on a compass rose.': 'Pusula gülünde yön (gidiş yönü).',
  'Top-down shape of the flight, with the current position marked.':
    'Uçuşun kuşbakışı şekli, mevcut konum işaretli.',
  'Distance and bearing from the takeoff point, radar-style.':
    'Kalkış noktasından mesafe ve kerteriz, radar tarzı.',
  'Live camera settings: ISO, shutter, aperture, EV, white balance, focal length.':
    'Canlı kamera ayarları: ISO, enstantane, diyafram, EV, beyaz ayarı, odak uzaklığı.',
  'How much of the flight was spent at each speed.': 'Uçuşun her hızda ne kadar geçtiği.',
  'How much time was spent at each altitude.': 'Her irtifada ne kadar zaman geçtiği.',
  'RGB colour distribution of the current video frame.': 'Mevcut video karesinin RGB renk dağılımı.',
  'Which compass directions the drone flew toward most.': 'Drone’un en çok hangi yönlere uçtuğu.',
  'How altitude and speed relate — each dot is one moment.':
    'İrtifa ile hız ilişkisi — her nokta bir an.',
  'Altitude against distance from home — the flight’s vertical profile outward.':
    'İrtifaya karşı eve mesafe — uçuşun dışa doğru dikey profili.',
  'The path in 3D at real altitude — drag to orbit.': '3B’de gerçek irtifada iz — döndürmek için sürükle.',
  'Peak moments (max altitude, top speed, farthest…) with values — click to jump.':
    'Tepe anları (maks irtifa, tepe hız, en uzak…) değerleriyle — atlamak için tıkla.',
};

const LANG_KEY = 'drone-player-lang';
function initialLang(): Lang {
  try {
    return localStorage.getItem(LANG_KEY) === 'tr' ? 'tr' : 'en';
  } catch {
    return 'en';
  }
}

/**
 * Keep <html lang> in step with the UI language. This is a rendering rule, not metadata: CSS
 * `text-transform: uppercase` applies the CASING RULES OF THE DECLARED LANGUAGE, and Turkish is a
 * dotless-i language, so under lang="tr" an English heading like "View" uppercases to "VİEW" and
 * "Terrain" to "TERRAİN". The panel is full of uppercased section headings, so a language switch
 * that leaves the tag behind mis-renders every one of them. It also decides hyphenation, quotation
 * marks and which voice a screen reader uses.
 */
function applyDocumentLang(l: Lang): void {
  if (typeof document !== 'undefined') document.documentElement.lang = l;
}

class I18n {
  // Private + accessor rather than a bare field so that EVERY way of changing the language goes
  // through one place. `i18n.lang = 'tr'` is a real call site (havasaha pins the site to Turkish);
  // with the sync living only in setLang() that assignment would silently skip it.
  #lang = $state<Lang>(initialLang());
  constructor() {
    applyDocumentLang(this.#lang);
  }
  get lang(): Lang {
    return this.#lang;
  }
  set lang(l: Lang) {
    this.#lang = l;
    applyDocumentLang(l);
    try {
      localStorage.setItem(LANG_KEY, l);
    } catch {
      /* storage unavailable */
    }
  }
  t(en: string): string {
    return this.lang === 'tr' ? (TR[en] ?? en) : en;
  }
  setLang(l: Lang): void {
    this.lang = l;
  }
  toggle(): void {
    this.setLang(this.lang === 'en' ? 'tr' : 'en');
  }
}

export const i18n = new I18n();
/** Translate an English UI string to the active language. */
export const t = (en: string): string => i18n.t(en);
